/**
 * IntegrationsPage — account connections (Phase 5 rebuild 2026-05-30; the
 * Snippet + WhatsApp widget moved to WebsitePage 2026-06-09 so this page stays
 * focused on accounts you connect).
 *
 * Sections:
 *   1. Status overview (5-dot row — still includes the Snippet dot as a summary)
 *   2. Ad platforms — GoogleAdsCard + FacebookAdsCard (showFireCounts mode)
 *   3. WhatsApp — WhatsAppConnectCard (not paired) → WhatsAppConnectedCard
 *      (paired, skipSubscribe=true since the subscription is already live)
 *   4. Analytics — IntegrationsGoogleAnalyticsCard (mirrors Google Ads state)
 *
 * Skip semantics: each card's skip checkbox writes immediately to
 * /api/onboarding/skip; me.*_skipped_at drives the checked state; the handler
 * is one-way (no-op on uncheck). To clear: press Connect.
 *
 * No Disconnect in UI per the 2026-04-29 product stance (push disconnect to the
 * platform side). "Add another WhatsApp number" hidden until Multi-WA Pro ships.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { Banner } from '@/components/Banner';
import { PageHeader } from '@/components/PageHeader';
import { GoogleAdsCard } from '@/components/connect/GoogleAdsCard';
import { FacebookAdsCard } from '@/components/connect/FacebookAdsCard';
import { WhatsAppConnectCard } from '@/components/golive/WhatsAppConnectCard';
import { WhatsAppConnectedCard } from '@/components/golive/WhatsAppConnectedCard';
import { IntegrationsGoogleAnalyticsCard } from '@/components/integrations/IntegrationsGoogleAnalyticsCard';
import { IntegrationsStatusOverview } from '@/components/integrations/IntegrationsStatusOverview';
import { SkeletonBox, SkeletonGroup } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';

interface StatsTotals {
  conversions_sent_total: number;
  conversions_sent_google: number;
  conversions_sent_facebook: number;
}

interface StatsResponse {
  totals: StatsTotals;
}

export function IntegrationsPage() {
  const { me, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [forceReconnectWhatsapp, setForceReconnectWhatsapp] = useState(false);
  const [whatsappWillSkipLocal, setWhatsappWillSkipLocal] = useState(false);

  // Fire counts for the connected ad cards (scoped to the impersonation
  // context via the existing /api/stats endpoint).
  const statsQuery = useQuery({
    queryKey: ['stats'],
    queryFn: () => apiRequest<StatsResponse>('/stats'),
    enabled: !authLoading,
    staleTime: 30_000,
  });

  const skipMutation = useMutation({
    meta: { errorMessage: "Couldn't save that — try again" },
    mutationFn: (step: 'google_ads' | 'facebook' | 'go_live') =>
      apiRequest('/onboarding/skip', {
        method: 'POST',
        body: { step },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  // Renders ✓ reflecting me.*_skipped_at; we never fire on uncheck.
  function makeSkipHandler(step: 'google_ads' | 'facebook') {
    return (next: boolean) => {
      if (!next) return;
      skipMutation.mutate(step);
    };
  }

  if (authLoading) {
    return <IntegrationsPageSkeleton />;
  }
  if (!me) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <ErrorState title="Couldn't load your account" />
      </div>
    );
  }

  const googleWillSkip = !!me.google_ads_skipped_at;
  const facebookWillSkip = !!me.facebook_skipped_at;
  const whatsappPaired = !!me.waba_id;
  const totals = statsQuery.data?.totals;
  const gaClientId = me.memberships?.[0]?.client_id;

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <PageHeader
        title="Integrations"
        subtitle="Everything that needs to be connected for HeyMotek to match your contacts and fire conversions back to your ad platforms."
      />

      {/* 1. Status overview */}
      <IntegrationsStatusOverview />

      {/* 2. Ad platforms */}
      <div>
        <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Ad platforms
        </h2>
        <div className="space-y-3">
          <GoogleAdsCard
            willSkip={googleWillSkip}
            onWillSkipChange={makeSkipHandler('google_ads')}
            showFireCounts
            firesToday={totals?.conversions_sent_google ?? 0}
            firesLast30d={totals?.conversions_sent_google ?? 0}
          />
          <FacebookAdsCard
            willSkip={facebookWillSkip}
            onWillSkipChange={makeSkipHandler('facebook')}
            showFireCounts
            firesToday={totals?.conversions_sent_facebook ?? 0}
            firesLast30d={totals?.conversions_sent_facebook ?? 0}
          />
        </div>
      </div>

      {/* 3. WhatsApp */}
      <div>
        <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          WhatsApp
        </h2>
        {whatsappPaired && !forceReconnectWhatsapp ? (
          <WhatsAppConnectedCard
            phoneNumber={me.whatsapp_number}
            skipSubscribe
            onReconnect={() => setForceReconnectWhatsapp(true)}
          />
        ) : (
          <>
            {forceReconnectWhatsapp && (
              <div className="mb-3">
                <Banner variant="warn" size="compact" icon={AlertTriangle}>
                  Pairing a new WhatsApp number replaces the current connection.
                </Banner>
              </div>
            )}
            <WhatsAppConnectCard
              willSkip={whatsappWillSkipLocal || !!me.go_live_skipped_at}
              onWillSkipChange={(next) => {
                if (!next) return;
                setWhatsappWillSkipLocal(true);
                skipMutation.mutate('go_live');
              }}
            />
          </>
        )}
      </div>

      {/* 4. Analytics */}
      <div>
        <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Analytics
        </h2>
        {gaClientId && (
          <IntegrationsGoogleAnalyticsCard clientId={gaClientId} />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Loading skeleton
// ──────────────────────────────────────────────────────────────────────

function IntegrationsPageSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <SkeletonGroup className="space-y-6">
        <div>
          <SkeletonBox tone="prominent" className="h-10 w-48 sm:h-12" />
          <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
        </div>
        <SkeletonBox className="h-28 w-full rounded-3xl" />
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBox key={i} className="h-40 w-full rounded-3xl" />
        ))}
      </SkeletonGroup>
    </div>
  );
}
